import { Badge, Icon, IconButton, Image } from "components";
import { Loading } from "containers";
import { sortHashtags, startViewTransition } from "helpers";
import { useAxios } from "hooks";
import { Layout } from "layouts";
import { isEqual, sortBy } from "lodash";
import Error404 from "pages/Error404";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Magazine } from "types";
import * as Attributes from "./Attributes";

export default function ProductDetails() {
  const navigate = useNavigate();
  const [axios, loading] = useAxios();
  const [searchParams] = useSearchParams();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [product, setProduct] = useState<Magazine.ProductDetails | null>(null);

  const userId = searchParams.get("userId");
  const productId = searchParams.get("productId");

  const images = useMemo(() => {
    return sortBy(product?.images, (e) => e.displayOrder);
  }, [product?.images]);
  const activeImage = images.at(activeImageIndex);

  const handleSetActiveImageIndex = (index: number) => {
    return () => {
      if (isEqual(index, activeImageIndex)) return;
      startViewTransition(() => {
        flushSync(() => {
          setActiveImageIndex(index);
        });
      });
    };
  };
  const copyCode = () => {
    navigator.clipboard
      .writeText(product?.code ?? "")
      .then(() => {
        toast.success("Copied");
      })
      .catch(() => {
        toast.error("Failed");
      });
  };
  const goBack = () => {
    navigate(-1);
  };
  const getProduct = () => {
    if (!userId || !productId) return;
    const url = `/People/${userId}/get-public-products/${productId}`;
    axios.get(url).then(({ data }) => {
      setProduct(data);
    });
  };
  useEffect(getProduct, [userId, productId]);
  if (loading) return <Loading />;
  if (!product) return <Error404 />;
  return (
    <Layout>
      <Layout.Header
        className="bg-white"
        divProps={{ className: "flex items-center gap-2" }}
      >
        <IconButton type="button" variant="text" color="dark" onClick={goBack}>
          <Icon name="ArrowLeft2" />
        </IconButton>
        <h6 className="text-center flex-1 text-base font-semibold text-dark">
          Product details
        </h6>
        <IconButton type="button" className="invisible">
          <Icon name="ArrowLeft2" />
        </IconButton>
      </Layout.Header>
      <Layout.Body className="space-y-5">
        <section>
          <div className="space-y-2 py-2.5">
            <h3 className="text-sm font-medium text-dark">{product.title}</h3>
            <p className="text-sm font-medium text-[#5E6278]">
              {product.category}
            </p>
          </div>
          <div className="space-y-2.5">
            {activeImage && (
              <Image
                key={activeImageIndex}
                src={activeImage.url}
                alt={product.title}
                className="w-full aspect-image rounded object-cover"
                style={{
                  viewTransitionName: `product-image-${activeImageIndex}`,
                }}
              />
            )}
            <div className="grid grid-cols-4 gap-1">
              {images.map((e, i) => {
                const isActive = i === activeImageIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    className={twMerge(
                      "block w-full aspect-image cursor-pointer rounded overflow-hidden transition-all",
                      isActive
                        ? "border-[3px] border-solid border-primary"
                        : "border-0",
                    )}
                    onClick={handleSetActiveImageIndex(i)}
                  >
                    <Image
                      src={e.thumbnailUrl}
                      alt={product.title}
                      className="size-full object-cover"
                      style={{
                        viewTransitionName: isActive
                          ? "none"
                          : `product-image-${i}`,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-base font-normal text-[#5E6278] whitespace-pre-wrap py-2.5 empty:hidden">
            {product.description}
          </p>
          <div className="flex flex-wrap gap-1 py-1 empty:hidden">
            {sortHashtags(product.hashtag)?.map((e) => (
              <Badge key={e}>#{e}</Badge>
            ))}
          </div>
        </section>
        <div className="w-full h-2.5 flex items-center">
          <hr className="border-[#ECEFF2] w-full" />
        </div>
        <details open className="group">
          <summary className="text-sm font-medium text-primary py-2.5 list-none">
            Attributes{" "}
            <Icon
              name="ArrowDown2"
              variant="Bold"
              className="size-4 transition-transform group-[[open]]:rotate-180"
            />
          </summary>
          <section>
            <Attributes.Container>
              {product.attributes?.map((e) => (
                <Attributes.Item key={e.title}>
                  <Attributes.Title>{e.title}</Attributes.Title>
                  <Attributes.Value>{e.value}</Attributes.Value>
                </Attributes.Item>
              ))}
              <Attributes.Item>
                <Attributes.Title>Product code</Attributes.Title>
                <Attributes.Value>
                  <button
                    type="button"
                    onClick={copyCode}
                    className="w-full flex items-center justify-between gap-2"
                  >
                    <span className="text-base">{product.code}</span>
                    <Icon name="Copy" className="size-6" />
                  </button>
                </Attributes.Value>
              </Attributes.Item>
            </Attributes.Container>
          </section>
        </details>
      </Layout.Body>
      <Layout.Footer />
    </Layout>
  );
}
